import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, Check, ChevronLeft, Loader2 } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';

export interface EtapaDoAssistente {
  /** Slug estável. Entra no `id` do cabeçalho da etapa. */
  id: string;
  /** Rótulo curto da etapa (ex.: "Licenças SGTP"). */
  titulo: string;
  /** Uma linha explicando o que se preenche aqui. */
  descricao?: string;
}

export interface AssistenteDeEtapasProps {
  etapas: readonly EtapaDoAssistente[];
  /** Índice da etapa corrente (base 0). CONTROLADO por quem chama. */
  etapaAtual: number;
  /** Índices já concluídos — só eles viram botão no indicador. */
  concluidas: ReadonlySet<number>;
  /** Validação da etapa corrente. `false` cancela o avanço. Pode ser assíncrona. */
  aoAvancar: () => boolean | Promise<boolean>;
  /** Volta uma etapa. Nunca valida. */
  aoVoltar: () => void;
  /** Ausente = o indicador não navega, nem nas etapas concluídas. */
  aoIrPara?: (indice: number) => void;
  /** Rótulo do botão primário na última etapa (ex.: "Enviar inscrição"). */
  rotuloDaConclusao: string;
  aoConcluir: () => void;
  /** Ex.: "≈ 8 min". Some da vista abaixo de `sm`. */
  tempoEstimado?: string;
  /** Nome acessível da lista de etapas (ex.: "Etapas do autocadastro"). */
  rotuloAcessivel: string;
  /** Barra de rascunho, avisos — acima do indicador. */
  cabecalho?: ReactNode;
  /** Ex.: "Salvar rascunho" — no rodapé, entre "Voltar" e o botão primário. */
  acoesSecundarias?: ReactNode;
  /** O conteúdo da etapa corrente. */
  children: ReactNode;
  className?: string;
}

/** O que a região viva diz quando `aoAvancar` recusa o avanço. */
const AVISO_DE_VALIDACAO = 'Confira os campos destacados';

/** Rótulo do botão primário enquanto `aoAvancar` não resolve. */
const ROTULO_DE_ESPERA = 'Aguarde…';

function anunciarEtapa(indice: number, total: number, titulo: string): string {
  return `Etapa ${indice + 1} de ${total}: ${titulo}`;
}

/**
 * Assistente de etapas — o passo a passo de formulários longos (autocadastro do
 * transportador, inscrição de credenciamento).
 *
 * O design system já tinha visualizadores de progresso (`ProgressTimeline`,
 * `TimelineBadgeStepper`), mas nenhum deles governa etapa nem navegação: são
 * leitura de um processo que acontece fora da tela. Aqui a etapa corrente é
 * estado da tela, e o componente é dono do indicador, do rodapé e — principal
 * motivo de existir — do contrato de acessibilidade.
 *
 * Duas escolhas que parecem detalhe e não são:
 *
 * 1. **Não é `tablist`/`tab`/`tabpanel`.** Esse papel promete ao leitor de tela
 *    navegação por setas entre as abas, que não implementamos. Lista ordenada
 *    com `aria-current="step"` descreve o que a tela realmente faz.
 * 2. **Um markup só nos dois tamanhos.** Abaixo de `sm`, o rótulo das etapas
 *    que não são a corrente fica `sr-only` — some da vista, permanece no
 *    leitor. Duplicar a lista com `hidden sm:block` faria o leitor de tela
 *    ouvir as etapas duas vezes.
 */
export function AssistenteDeEtapas({
  etapas,
  etapaAtual,
  concluidas,
  aoAvancar,
  aoVoltar,
  aoIrPara,
  rotuloDaConclusao,
  aoConcluir,
  tempoEstimado,
  rotuloAcessivel,
  cabecalho,
  acoesSecundarias,
  children,
  className,
}: AssistenteDeEtapasProps) {
  const total = etapas.length;
  const indice = Math.min(Math.max(etapaAtual, 0), Math.max(total - 1, 0));
  const etapa = etapas[indice];

  const [pendente, setPendente] = useState(false);
  const [anuncio, setAnuncio] = useState('');

  const tituloRef = useRef<HTMLHeadingElement>(null);
  const primeiraRenderizacao = useRef(true);

  const tituloDaEtapa = etapa?.titulo ?? '';

  useEffect(() => {
    setAnuncio(anunciarEtapa(indice, total, tituloDaEtapa));
  }, [indice, total, tituloDaEtapa]);

  // O foco vai para o cabeçalho da etapa a cada troca — nunca na montagem, pra
  // não roubar o foco de quem acabou de abrir a tela. `autoFocus` não serviria:
  // além de a regra `jsx-a11y/no-autofocus` estar ligada fora de `ui/base`, ele
  // só agiria na primeira renderização, que é justamente o caso que não queremos.
  useEffect(() => {
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }
    tituloRef.current?.focus();
  }, [indice]);

  const ultima = indice === total - 1;
  const primeira = indice === 0;

  const aoClicarNoPrimario = useCallback(async () => {
    if (ultima) {
      aoConcluir();
      return;
    }

    setPendente(true);
    try {
      const avancou = await aoAvancar();
      if (avancou === false) setAnuncio(AVISO_DE_VALIDACAO);
    } finally {
      setPendente(false);
    }
  }, [aoAvancar, aoConcluir, ultima]);

  if (!etapa) return null;

  const idDoTitulo = `${etapa.id}-titulo`;
  const percentual = Math.round(((indice + 1) / total) * 100);

  return (
    <div className={cn('space-y-6', className)}>
      <p role="status" aria-live="polite" className="sr-only">
        {anuncio}
      </p>

      {cabecalho}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-muted-foreground" aria-hidden="true">
            Etapa {indice + 1} de {total}
          </p>
          {tempoEstimado && (
            // `sr-only sm:not-sr-only`, e não `hidden sm:block`: some da vista no
            // celular sem sumir do leitor de tela.
            <p className="sr-only text-xs text-muted-foreground sm:not-sr-only">
              Tempo estimado: {tempoEstimado}
            </p>
          )}
        </div>

        {/* A lista abaixo e a região viva já dizem em que etapa se está: a barra
            é redundância visual, e redundância anunciada vira ruído. */}
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${percentual}%` }}
          />
        </div>

        {/* Uma linha só no celular (375 px): as etapas não correntes mostram só o
            número, e o rótulo da corrente encurta com reticências antes de
            empurrar a última etapa para baixo — o título inteiro está logo
            abaixo, no cabeçalho da etapa. A partir de `sm`, volta a quebrar. */}
        <ol
          aria-label={rotuloAcessivel}
          className="flex flex-nowrap items-center gap-y-2 sm:flex-wrap"
        >
          {etapas.map((cada, posicao) => {
            const corrente = posicao === indice;
            const concluida = concluidas.has(posicao) && !corrente;
            const navegavel = concluida && Boolean(aoIrPara);

            const circulo = (
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                  corrente &&
                    'bg-primary text-primary-foreground ring-2 ring-primary/40 ring-offset-2 ring-offset-background',
                  concluida && 'bg-success text-success-foreground',
                  !corrente && !concluida && 'bg-muted text-muted-foreground',
                )}
              >
                {concluida ? <Check className="h-3.5 w-3.5" /> : posicao + 1}
              </span>
            );

            const rotulo = (
              <span
                className={cn(
                  'text-sm',
                  // Só a etapa corrente mostra o rótulo no celular. As outras
                  // continuam no leitor de tela e voltam à vista a partir de `sm`.
                  corrente
                    ? 'min-w-0 truncate font-medium text-foreground sm:overflow-visible sm:whitespace-normal'
                    : 'sr-only sm:not-sr-only',
                  concluida && 'sm:text-foreground',
                  !corrente && !concluida && 'sm:text-muted-foreground',
                )}
              >
                {cada.titulo}
                {concluida && <span className="sr-only">, concluída</span>}
              </span>
            );

            return (
              <li
                key={cada.id}
                aria-current={corrente ? 'step' : undefined}
                className={cn('flex items-center', corrente && 'min-w-0')}
              >
                {navegavel ? (
                  <button
                    type="button"
                    onClick={() => aoIrPara?.(posicao)}
                    className="flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {circulo}
                    {rotulo}
                  </button>
                ) : (
                  // Etapa futura é TEXTO, não botão desabilitado: botão
                  // desabilitado entra na varredura de controles do leitor de
                  // tela e promete uma ação que não existe.
                  <span className="flex min-w-0 items-center gap-2 px-1 py-1">
                    {circulo}
                    {rotulo}
                  </span>
                )}

                {posicao < total - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mx-1 h-px w-3 shrink-0 rounded-full sm:w-6',
                      posicao < indice ? 'bg-success/50' : 'bg-border',
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <section role="group" aria-labelledby={idDoTitulo} className="space-y-4">
        <div className="space-y-1">
          <h2
            id={idDoTitulo}
            ref={tituloRef}
            tabIndex={-1}
            className="rounded-sm text-base font-semibold text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {etapa.titulo}
          </h2>
          {etapa.descricao && <p className="text-sm text-muted-foreground">{etapa.descricao}</p>}
        </div>

        {children}
      </section>

      {/* A ordem do DOM é a ordem de leitura (Voltar, secundárias, primário). No
          celular a coluna inverte a PINTURA, não a leitura; no desktop o primário
          vai à direita por `sm:ml-auto`, nunca por `flex-row-reverse`. */}
      <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:items-center">
        {/* Na primeira etapa não há para onde voltar, então o botão não é
            renderizado (CLAUDE.md, "Regras de interface", item 7). */}
        {!primeira && (
          <Button
            type="button"
            variant="outline"
            onClick={aoVoltar}
            disabled={pendente}
            className="w-full sm:w-auto"
          >
            <ChevronLeft aria-hidden="true" />
            Voltar
          </Button>
        )}

        {acoesSecundarias && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">{acoesSecundarias}</div>
        )}

        <Button
          type="button"
          onClick={aoClicarNoPrimario}
          disabled={pendente}
          className="w-full sm:ml-auto sm:w-auto"
        >
          {pendente && (
            <>
              <Loader2 className="animate-spin" aria-hidden="true" />
              {ROTULO_DE_ESPERA}
            </>
          )}
          {!pendente && ultima && (
            <>
              <Check aria-hidden="true" />
              {rotuloDaConclusao}
            </>
          )}
          {!pendente && !ultima && (
            <>
              Próximo
              <ArrowRight aria-hidden="true" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export interface EstadoDoAssistente {
  etapaAtual: number;
  concluidas: ReadonlySet<number>;
  avancar: () => void;
  voltar: () => void;
  irPara: (indice: number) => void;
  /**
   * Abre direto numa etapa adiante, como quem retoma um rascunho salvo: todas
   * as etapas anteriores contam como concluídas. `irPara` não serve para isso
   * porque só muda a etapa corrente, e o indicador perderia o ✓ das etapas que
   * o usuário já preencheu em outra visita.
   */
  retomar: (indice: number) => void;
}

/**
 * Estado de etapa para quem não quer governá-lo à mão. Guarda etapa corrente e
 * concluídas num objeto só, porque avançar muda as duas ao mesmo tempo e duas
 * chamadas de `setState` separadas abririam uma renderização intermediária em
 * que a etapa já andou mas a anterior ainda não consta como concluída.
 *
 * Voltar NÃO desmarca a etapa concluída: quem já preencheu e validou continua
 * podendo pular direto pelo indicador.
 *
 * Mora no mesmo arquivo do componente de propósito: é a outra metade do mesmo
 * contrato, e quem usa o assistente importa os dois juntos. Separar só pra
 * agradar o recarregamento a quente esconderia a relação — o preço é hot-reload
 * menos granular em desenvolvimento, sem efeito nenhum em produção. Mesma troca
 * já aceita em `src/ui/base` para os `xxxVariants` do shadcn/ui
 * (eslint.config.mjs), daí o desligamento pontual da regra abaixo.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAssistenteDeEtapas(total: number): EstadoDoAssistente {
  const [estado, setEstado] = useState<{ etapaAtual: number; concluidas: ReadonlySet<number> }>(
    () => ({ etapaAtual: 0, concluidas: new Set<number>() }),
  );

  const avancar = useCallback(() => {
    setEstado((anterior) => {
      if (anterior.etapaAtual >= total - 1) return anterior;
      const concluidas = new Set(anterior.concluidas);
      concluidas.add(anterior.etapaAtual);
      return { etapaAtual: anterior.etapaAtual + 1, concluidas };
    });
  }, [total]);

  const voltar = useCallback(() => {
    setEstado((anterior) =>
      anterior.etapaAtual === 0 ? anterior : { ...anterior, etapaAtual: anterior.etapaAtual - 1 },
    );
  }, []);

  const irPara = useCallback(
    (indice: number) => {
      setEstado((anterior) => {
        if (indice < 0 || indice > total - 1 || indice === anterior.etapaAtual) return anterior;
        return { ...anterior, etapaAtual: indice };
      });
    },
    [total],
  );

  const retomar = useCallback(
    (indice: number) => {
      setEstado((anterior) => {
        if (indice < 0 || indice > total - 1) return anterior;
        const concluidas = new Set(anterior.concluidas);
        for (let etapa = 0; etapa < indice; etapa += 1) concluidas.add(etapa);
        return { etapaAtual: indice, concluidas };
      });
    },
    [total],
  );

  return {
    etapaAtual: estado.etapaAtual,
    concluidas: estado.concluidas,
    avancar,
    voltar,
    irPara,
    retomar,
  };
}

export default AssistenteDeEtapas;
